import React, { useMemo, useRef, useState } from 'react';
import { useMount } from 'react-use';
import { Box } from '@mui/material';

import ErrorBoundary from 'components/ErrorBoundary';
import ErrorMessage from 'components/ErrorMessage';
import useCustomError from 'hooks/useCustomError';
import type { ConditionData } from 'types/conditionRating';
import type { ConsistencyData } from 'types/consistency';
import type { SunlightTimes } from 'types/sunlightTimes';
import type { SurfData } from 'types/surf';
import type { SwellsData, SwellsTimestep } from 'types/swell';
import type { TideData } from 'types/tide';
import type { Weather } from 'types/weather';
import type { WindData } from 'types/wind';
import type { SubregionDaySummaryData } from 'types/subregion';
import type { WaveEnergyGraphData } from 'types/waveEnergy';
import { hasDisplayableWaveEnergyForDayIndices } from 'utils/waveEnergyTableDisplay';

import useTableData from '../hooks/useTableData';
import TableDayContainer from '../TableDayContainer';

import styles from './TableContainer.module.scss';

interface Props {
  daySummary: SubregionDaySummaryData;
  daysAllowed: number;
  isHistoricView: boolean;
  ratings: ConditionData;
  consistency: ConsistencyData;
  sunlightTimes: SunlightTimes;
  surf: SurfData;
  swells: SwellsData;
  tide: TideData;
  trackingDetails?: {
    spotName: string;
    spotId: string;
    subregionName: string;
    subregionId: string;
  };
  utcOffset: number;
  weather: Weather;
  wind: WindData;
  waveEnergy?: WaveEnergyGraphData;
}

const TableContainerContent: React.FC<Props> = ({
  daySummary,
  daysAllowed,
  isHistoricView,
  ratings,
  consistency,
  sunlightTimes,
  surf,
  swells,
  tide,
  trackingDetails,
  utcOffset,
  weather,
  wind,
  waveEnergy,
}) => {
  const [isMounted, setIsMounted] = useState(false);
  const [showObservedData, setShowObservedData] = useState<boolean>(true);
  const containerRef = useRef<HTMLDivElement>(null);

  const {
    daysExpanded,
    showHourlyWhenExpanded,
    ratingsDays,
    consistencyDays,
    toggleDayExpanded,
    toggleDayHourlyExpanded,
    surfDays,
    swellsDays,
    weatherDays,
    windDays,
  } = useTableData({
    numDaysToDisplay: daysAllowed,
    ratings,
    consistency,
    surf,
    swells,
    trackingDetails,
    utcOffset,
    weather,
    wind,
  });

  const units = useMemo(
    () => ({
      surf: surf?.units,
      wave: swells?.units,
      wind: wind?.units,
      weather: weather?.units,
    }),
    [surf?.units, swells?.units, weather?.units, wind?.units],
  );

  const hideProbability = useMemo(
    () =>
      swellsDays?.every((day: Array<SwellsTimestep>) =>
        day?.every((hour: SwellsTimestep) => hour?.probability === null),
      ),
    [swellsDays],
  );

  const useNearshoreFromWaveEnergy = useMemo(
    () =>
      hasDisplayableWaveEnergyForDayIndices(
        waveEnergy,
        Array.from({ length: daysAllowed }, (_, i) => i),
      ),
    [daysAllowed, waveEnergy],
  );

  useMount(() => {
    /*
      This prevents the table contents from blocking the main rendering thread.
      The animation of the Switch toggle is 50ms and so waiting at least 50 ms prevents
      jumping or lagging when toggling the switch. setting to 80 to give an extra 30ms of padding.
    */
    setTimeout(() => {
      setIsMounted(true);
    }, 80);
  });

  return isMounted ? (
    <Box className={styles.container} data-testid="forecast-table-v2" ref={containerRef}>
      <Box className={styles.daysContainer}>
        {(swellsDays?.length ? swellsDays : []).map((swellsDay, dayIndex) =>
          swellsDay?.length ? (
            <TableDayContainer
              dayIndex={dayIndex}
              daySummary={daySummary}
              hideProbability={hideProbability}
              isExpanded={daysExpanded[dayIndex]}
              isHourlyExpanded={showHourlyWhenExpanded}
              isHistoricView={isHistoricView}
              isLoading={surf?.loading || swells?.loading || !!waveEnergy?.loading}
              key={`table-day-container-${dayIndex + 1}`}
              observations={surf?.observations}
              onExpandCollapse={toggleDayExpanded}
              onToggleHourly={toggleDayHourlyExpanded}
              ratingDay={ratingsDays?.[dayIndex]}
              consistencyDay={consistencyDays?.[dayIndex]}
              spotName={trackingDetails?.spotName}
              subregionId={trackingDetails?.subregionId}
              sunlightTimes={sunlightTimes}
              tide={tide}
              units={units}
              surfDay={surfDays?.[dayIndex]}
              swellsDay={swellsDay}
              weatherDay={weatherDays?.[dayIndex]}
              weatherIconPath={weather?.weatherIconPath}
              windDay={windDays?.[dayIndex]}
              spotId={trackingDetails?.spotId}
              subregionName={trackingDetails?.subregionName}
              utcOffset={utcOffset}
              showObservedData={showObservedData}
              onShowObservedDataChange={setShowObservedData}
              useNearshoreFromWaveEnergy={useNearshoreFromWaveEnergy}
              waveEnergy={waveEnergy}
            />
          ) : null,
        )}
      </Box>
    </Box>
  ) : (
    <div className={styles.tableContainerPlaceholder} />
  );
};

/**
 * Main container for ForecastTableV2
 * <ForecastTableV2 ...props />
 */
const TableContainer: React.FC<Props> = ({ ...restProps }) => {
  const { errorMessage, ErrorIcon, hasError } = useCustomError({
    error: restProps?.surf?.error || restProps?.swells?.error,
  });

  return (
    <ErrorBoundary
      error={hasError}
      errorBoundaryId="forecast-table-container"
      render={() => (
        <Box className={styles.errorBoundaryContainer} data-testid="forecast-table-error">
          <ErrorMessage Icon={ErrorIcon} message={errorMessage} />
        </Box>
      )}
    >
      <TableContainerContent {...restProps} />
    </ErrorBoundary>
  );
};

export default TableContainer;
